/**
 * The org's own clock, and the one rule about how long a shift can run.
 *
 * Direct counterpart of hrms-backend/src/utils/timezone.js (the offset and the
 * day boundary) and hrms-backend/src/utils/sessions.js (HALF_DAY_MINUTES). If
 * either of those changes, this changes in the same commit — the same standing
 * arrangement lib/calendar.ts already has with the backend's utils/calendar.js.
 *
 * NOTE the backend reads its two clock constants from the environment, while
 * these are compile-time literals with no runtime feed: setting
 * ORG_TZ_OFFSET_MINUTES or DAY_BOUNDARY_HOUR on the server desynchronises every
 * date this file computes, silently. Serving them to the client would close
 * that gap.
 *
 * ---------------------------------------------------------------------------
 * Why the browser's timezone is not used
 * ---------------------------------------------------------------------------
 * Mentoria runs on IST, and the backend decides every calendar date and stores
 * every timestamp against that fixed offset regardless of what timezone its host
 * is set to. The frontend used to disagree on both counts: `todayStr()` read the
 * browser's date, and check-in times were rendered with the browser's offset.
 *
 * On an IST machine the two agree and nothing looks wrong, which is exactly why
 * it survived. Anywhere else they drift: HR's "Today's roster" filters on a date
 * the backend never wrote, so it comes back empty, and a 9:10 am check-in renders
 * as some other hour entirely. Both read as the data being wrong rather than the
 * viewer's clock, so both are pinned to the org's offset here.
 */

/** IST, UTC+05:30. Matches ORG_TZ_OFFSET_MINUTES in the backend's timezone.js. */
export const ORG_TZ_OFFSET_MINUTES = 330;

/**
 * The hour the org's day rolls over. Matches DAY_BOUNDARY_HOUR in the backend's
 * timezone.js.
 *
 * A shift from 22:00 to 03:00 is one day's work, so an event before this hour
 * belongs to the PREVIOUS calendar day — nobody is still at their desk after 6am,
 * so anything before it is the tail of the night before. Everything the backend
 * stores is filed under that rule, so anything here that looks a record up by
 * "today" has to use the same one or it asks for a day that was never written.
 */
export const DAY_BOUNDARY_HOUR = 6;

/** 4.5h in minutes — at or above this a day is full, below it half. */
export const HALF_DAY_MINUTES = 270;

interface OrgParts {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * An instant broken into the wall-clock parts someone in the org would read off
 * a clock on the wall. Works by shifting the instant and reading its UTC fields,
 * so the result never depends on the viewer's machine.
 */
function orgParts(value: string | Date): OrgParts | null {
  const instant = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  const shifted = new Date(instant.getTime() + ORG_TZ_OFFSET_MINUTES * 60_000);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hours: shifted.getUTCHours(),
    minutes: shifted.getUTCMinutes(),
    seconds: shifted.getUTCSeconds(),
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "2026-08-24" for an instant, in org time. */
export function orgDateStr(value: string | Date): string | null {
  const parts = orgParts(value);
  return parts ? `${parts.year}-${pad(parts.month)}-${pad(parts.day)}` : null;
}

/** Today's calendar date in org time, as "YYYY-MM-DD". */
export function orgTodayStr(): string {
  return orgDateStr(new Date()) as string;
}

/**
 * The current year in ORG time.
 *
 * `new Date().getFullYear()` is the viewer's year, which is a different number
 * on either side of the new year and on any machine with a wrong clock. Anything
 * labelled "this year" on screen should come from here.
 */
export function orgYear(): number {
  return Number(orgTodayStr().slice(0, 4));
}

/** Which work day an instant belongs to, applying the 6am boundary. */
export function workDateOf(value: string | Date): string | null {
  const parts = orgParts(value);
  if (!parts) return null;
  const day = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  if (parts.hours < DAY_BOUNDARY_HOUR) day.setUTCDate(day.getUTCDate() - 1);
  return day.toISOString().slice(0, 10);
}

/**
 * The work day in progress right now. Between midnight and DAY_BOUNDARY_HOUR this
 * is yesterday's date, which is the point: someone who checked in at 22:00 is
 * still inside that day's shift at 01:00.
 */
export function currentWorkDate(): string {
  return workDateOf(new Date()) as string;
}

/** "9:42 am", or "9:42:07 am" with seconds — 12-hour, org time. */
export function orgTimeStr(value: string | Date, withSeconds = false): string | null {
  const parts = orgParts(value);
  if (!parts) return null;
  const suffix = parts.hours < 12 ? "am" : "pm";
  const hour12 = parts.hours % 12 === 0 ? 12 : parts.hours % 12;
  const seconds = withSeconds ? `:${pad(parts.seconds)}` : "";
  return `${hour12}:${pad(parts.minutes)}${seconds} ${suffix}`;
}

/** "09:42", for an <input type="time"> — org time, so it round-trips. */
export function orgTimeInputValue(value: string | Date): string {
  const parts = orgParts(value);
  return parts ? `${pad(parts.hours)}:${pad(parts.minutes)}` : "";
}

/** Hours between two timestamps, or null if either is missing or unparseable. */
export function hoursSpanned(
  from: string | null | undefined,
  to: string | null | undefined,
): number | null {
  if (!from || !to) return null;
  const start = new Date(from).getTime();
  const end = new Date(to).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return (end - start) / 3_600_000;
}

/** "8h30" for a minute count. */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return "—";
  const hours = Math.floor(minutes / 60);
  return `${hours}h${String(minutes % 60).padStart(2, "0")}`;
}
